/**
 * Modulo per segnali acustici e vibrazione di emergenza/intervento
 * Utilizza Web Audio API per generare un suono istantaneo ad alto volume e senza dipendenze esterne.
 */

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioCtx) {
    audioCtx = new AudioContextClass();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

// Sblocca l'AudioContext al primo tocco/click dell'utente per rispettare l'Autoplay Policy dei browser mobili
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    } else if (!audioCtx) {
      getAudioContext();
    }
  };
  window.addEventListener('click', unlockAudio, { capture: true, passive: true });
  window.addEventListener('touchstart', unlockAudio, { capture: true, passive: true });
}

/**
 * Riproduce un doppio segnale acustico bitonale (allarme chiamata assegnata)
 */
export function playEmergencyAlert(): void {
  try {
    // 1. Vibrazione smartphone (se supportata)
    if ('vibrate' in navigator) {
      navigator.vibrate([300, 150, 300, 150, 400]);
    }

    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Primo tono (880 Hz - Nota A5)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    gain1.gain.setValueAtTime(0.7, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.25);

    // Secondo tono più acuto (1174.66 Hz - Nota D6)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1174.66, now + 0.28);
    gain2.gain.setValueAtTime(0.8, now + 0.28);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.65);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.28);
    osc2.stop(now + 0.65);

    // Terzo tono di conferma (1760 Hz - Nota A6)
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(1760, now + 0.7);
    gain3.gain.setValueAtTime(0.9, now + 0.7);
    gain3.gain.exponentialRampToValueAtTime(0.01, now + 1.2);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.start(now + 0.7);
    osc3.stop(now + 1.2);
  } catch (err) {
    console.warn('[audioAlert] Impossibile riprodurre avviso acustico:', err);
  }
}
